
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// composable
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'

// constants

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { InvoiceStatus } from '@/core/types/invoices.types'
import { invoices } from '@/core/realisations/invoices.realisation'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'search'],
    setup(props, { emit }) {
        const state = reactive({
            companyId: null as number | null,
            startTime: null as string | null,
            endTime: null as string | null,
            statusIds: [] as number[],
            invoiceNumber: null as string | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            statuses: new DataState<InvoiceStatus>({}),
        })

        const reset = () => {
            if (props.loading) return

            state.companyId = null
            state.startTime = null
            state.endTime = null
            state.statusIds = []
            state.invoiceNumber = null

            emit('reset')
        }

        const search = () => {
            if (props.loading) return
            emit('search', { ...state })
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await invoices.getInvoiceStatuses()

                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })

                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompany = async (search_query: string) => {
            await loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadStatuses()
        })

        return {
            state,
            data_state,
            loadStatuses,
            loadCompanies,
            loadMoreCompanies,
            searchCompany,
            unshiftFindedCompany,
            reset,
            search,
        }
    },
})
